<script lang="ts">
  import { checkKeywordAvailability } from '@/utils'
  import { onMount } from 'svelte'
  import Portal from 'svelte-portal'
  import { fade } from 'svelte/transition'
  import keywordsStore from '@/stores/keywords.svelte'

  let {
    shortCode
  } = $props()

  const checkAvailabilityDebounced = App.utils.debounce(checkAvailability, 400)
  let active = $state(false)
  let showingKeywordHelp = $state(null)
  let submitting = $state(false)
  let newKeyword = $state('')
  let keywordChecking = $state(false)
  let keywordTaken = $state(false)
  let keywordType = $state(null)
  let activeTabKey = $state('keywords-how')
  let keywordInput = $state(null)

  let keywordTypes = {
    'keywords-how': {
      name: 'How do Keywords work?',
      description: `A "keyword" is a unique word or term that people can text to our
        shortcode (${shortCode}) to trigger a system "action", like
        adding the contact to a group, or automatically replying with a
        message.`
    },
    'text-to-join': {
      name: 'Text to Join',
      description: `Text to Join is used to build a contact list.
        It's simple: A contact sends a text message to ${shortCode} using the
        keyword you pick and they'll be added. `
    },
    'autoresponder': {
      name: 'Autoresponder',
      description: `A contact can text message a keyword that you choose and immediately
        receive your custom reply. This reply can include web links,
        image links and even coupons.`
    },
    'text-to-download': {
      name: 'Text to Download',
      description: `This is great for app developers to market their app.
        It’s an autoresponder that has a link to your app in the Apple or
        Google Play store. Choose your keyword, type a response and drop
        your link in. It’s 1-2-3 easy.`
    }
  }

  $effect(() => {
    if (activeTabKey !== 'keywords-how') {
      keywordType = keywordTypes[activeTabKey].name
    }
  })

  let canSubmit = $derived(
    newKeyword.length &&
    !keywordTaken &&
    !keywordChecking &&
    !submitting
  )

  let keywordAvailable = $derived(newKeyword.length && !keywordTaken)
  let helpClasses = $derived(showingKeywordHelp ? 'magictime tinLeftOut' : 'magictime tinLeftIn')

  onMount(() => {
    keywordType = keywordType || keywordTypes['text-to-join'].name

    if (localStorage.showingKeywordHelp == undefined || localStorage.showingKeywordHelp == 'true') {
      showingKeywordHelp = true
    }
  })

  function cancel() {
    active = false
    newKeyword = ''
    document.querySelector('body').classList.remove('modal-showing')
  }

  function checkAvailability() {
    checkKeywordAvailability(newKeyword)
      .then(response => {
        keywordTaken = !response.data.available
      })
      .catch()
      .then(() => {
        keywordChecking = false
      })
  }

  function save(evt) {
    // return unless enter key
    if (evt?.key && evt.key !== 'Enter') {
      return
    }
    if (!newKeyword.length || !canSubmit) {
      return false
    }
    submitting = true

    keywordsStore.create({
      keyword: {
        keyword: newKeyword,
        keyword_type: keywordType
      }
    })
      .then(response => {
        location.href = `/keywords/${response.keyword.to_param}/edit`
        cancel()
      })
      .catch(error => {
        console.log('error: ', error)
      })
    window.setTimeout(() => {
      submitting = false
    }, 3000)
  }

  function showForm() {
    active = true
    keywordInput.focus()
    document.querySelector('body').classList.add('modal-showing')
  }

  function showHelp() {
    showingKeywordHelp = localStorage.showingKeywordHelp = true
  }

  function hideHelp() {
    showingKeywordHelp = localStorage.showingKeywordHelp = false
  }

  function typeBoxChanged(key) {
    activeTabKey = key
  }

  $effect(() => {
    newKeyword = newKeyword.replace(/\./g, '')
    keywordChecking = true
    checkAvailabilityDebounced()
  })
</script>

<div class="KeywordFormNew">
  <Portal target="#cta-portal">
    <button type="button" class="btn btn-link icon icon--question icon--keywords-help {helpClasses}" onclick={showHelp}
            aria-label="Show Help"></button>
    <button type="button" class="btn btn-success btn-rounded" onclick={showForm}>Add New Keyword</button>
  </Portal>

  {#if showingKeywordHelp}
    <div class="panel--instructions keyword-instructions" transition:fade>
      <button type="button" class="icon icon--close-x green" onclick={hideHelp} aria-label="Hide help"></button>

      <div class="tabs-component">
        <!-- Nav tabs -->
        <ul class="nav nav-tabs" role="tablist">
          {#each Object.entries(keywordTypes) as [key, value]}
            <li role="presentation" class:active={key === activeTabKey} onclick={() => activeTabKey = key}>
              <button type="button" aria-controls="{key}" role="tab"
                      class="btn btn-link">{keywordTypes[key].name}</button>
            </li>
          {/each}
        </ul>

        <!-- Tab panes -->
        <div class="tab-content">
          {#each Object.entries(keywordTypes) as [key, value]}
            <div role="tabpanel" class="tab-pane" class:active={key === activeTabKey} id="{key}">
              {keywordTypes[key].description}
            </div>
          {/each}
        </div>
      </div>

    </div>
  {/if}

  <Portal target="#modals-portal">
    <div class="keyword-form-new" class:active={active}>
      <div class="keyword-form-inner">
        <form action="/keywords" accept-charset="UTF-8" onsubmit={() => false}>
          <input name="utf8" type="hidden" value="✓">
          <div class="form-group">
            <div class="text-right">
              <button type="button" onclick={cancel} aria-label="Cancel" class="btn btn-cancel--round">
                &nbsp;
              </button>
            </div>
          </div>
          <div class="form-group mt-3">
            <div class="form-errors js-form-errors js-form-errors--keyword-null"></div>

            <label for="keyword_keyword">Keyword</label>
            <input class="form-control keyword-field"
                   bind:this={keywordInput}
                   bind:value={newKeyword}
                   required={true}
                   autocomplete="off"
                   type="text"
                   maxlength="16"
                   onkeydown={save}>
            <div class="keyword-availability--vue">
              <div class:d-none={!keywordChecking} class="checking">Checking...</div>
              {#if !keywordChecking && newKeyword.length}
                <div class:d-none={!keywordAvailable} class="available">Available</div>
                <div class:d-none={keywordAvailable} class="not-available">Not available</div>
              {/if}
            </div>
          </div>

          <div class="flex-row">
            {#each Object.entries(keywordTypes).slice(1) as [key, value], index (value.name)}
              <button type="button" class="cell-1 keyword-type-box"
                      class:active={key === activeTabKey || (index === 0 && activeTabKey === 'keywords-how')}
                      onclick={() => typeBoxChanged(key)}>
                <span class="h4">{value.name}</span>
                <span class="p">
                  {value.description}
                </span>
              </button>
            {/each}
          </div>

          <div class="form-group mt-3">
            <input type="button" value="Save" onclick={save} disabled={!canSubmit} class="btn btn-rounded btn-gray">
          </div>
        </form>
      </div>
    </div>
  </Portal>

</div>

<style lang="scss">
  .nav-tabs li {
    margin-bottom: .5em;

    + li {
      margin-left: 1em;
    }
  }

  .btn-link {
    color: rgb(94, 186, 50);
    font-size: 1em;
    margin: 0;
    padding: 0;
  }

  .icon--keywords-help {
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }

  .tab-content {
    padding-top: 1em;
  }

  .active > .h4 {
    color: var(--tsio-green);
  }
</style>
